import { useState, useEffect } from 'react';
import {
    IonItem, IonLabel, IonGrid, IonRow,
    IonCol, IonProgressBar
} from '@ionic/react';
import { storageManager } from '../utils/storageManager';
import './styles/StorageMonitor.css';

interface StorageMonitorProps {
    refreshTrigger?: number;
}

const StorageMonitor: React.FC<StorageMonitorProps> = ({ refreshTrigger = 0 }) => {
    const [usage, setUsage] = useState<number>(0);
    const [quota, setQuota] = useState<number>(0);
    const [percentUsed, setPercentUsed] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Check storage usage on mount and when refreshTrigger changes
    useEffect(() => {
        const checkStorage = async () => {
            setIsLoading(true);
            try {
                const storageStats = await storageManager.checkStorageUsed();
                setUsage(storageStats.usage);
                setQuota(storageStats.quota);
                setPercentUsed(storageStats.percentUsed);
            } catch (error) {
                console.error("Error checking storage:", error);
            } finally {
                setIsLoading(false);
            }
        };

        checkStorage();
    }, [refreshTrigger]);

    // Helper function to format bytes to readable format
    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Determine color based on usage percentage
    const getProgressColor = (): string => {
        if (percentUsed < 50) return 'success';
        if (percentUsed < 80) return 'warning';
        return 'danger';
    };

    return (
        <div className="storage-monitor">
            <IonItem lines="none">
                <IonLabel className="ion-text-wrap">
                    <IonGrid>
                        <IonRow>
                            <IonCol size="12">
                                <div className="progress-bar-container">
                                    <IonProgressBar
                                        value={percentUsed < 2.5 ? 0.025 : percentUsed / 100}
                                        color={getProgressColor()}
                                        className="storage-progress"
                                    ></IonProgressBar>
                                    {!isLoading && (
                                        <span className="progress-percentage">
                                            {`${formatBytes(usage)} of ${formatBytes(quota)} (${percentUsed.toFixed(1)}%)`}
                                        </span>
                                    )}
                                </div>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonLabel>
            </IonItem>
        </div>
    );
};

export default StorageMonitor;